const article = {
	slug: "weighing-the-moon",
	title: "Weighing the Moon, fifteen years after I missed its launch",
	date: "13 August 2026",
	updated: null,
	description:
		"I was at Kennedy Space Center for NASA's GRAIL launch in 2011 and never saw it fly. Fifteen years later I built an interactive tribute to the mission that mapped the Moon's gravity.",
	keywords: ["grail mission", "interactive data visualization", "three.js globe", "nasa moon gravity", "moonkam"],
	tldr:
		"NASA's GRAIL mission mapped the Moon's gravity with twin spacecraft and let schoolchildren aim the cameras. I built a site to explore it: a spinnable gravity globe and a recreated MoonKAM image request, using only real NASA data and imagery.",
	readingTime: null,
	provenance: "Written from my build notes, August 2026.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	hero: {
		src: "/articles-media/weighing-the-moon.jpg",
		alt: "GRAIL's free-air gravity map of the Moon",
		caption: "GRAIL's real gravity map. Red pulls harder, blue pulls less. NASA/JPL-Caltech/MIT/GSFC.",
	},
	markdown: `In September 2011, NASA invited me to Kennedy Space Center to watch a rocket leave for the Moon. I was in my early twenties, a few years into life in the United States, and I did not quite believe the email was real.

The mission was GRAIL. Then the launch scrubbed. I had a flight to catch, so I flew home the day before it finally lifted off, and I never saw the rocket leave the pad.

Fifteen years later, I built the thing I wish I could have handed that younger version of myself: a way to stand inside the mission from a browser. It lives at [grail.swapniltamse.com](https://grail.swapniltamse.com).

## What GRAIL actually did

GRAIL was two spacecraft, not one. They were named Ebb and Flow, and they chased each other around the Moon in the same orbit.

The trick was the distance between them. A mountain, or a buried mass of dense rock, would pull on the lead spacecraft first and the trailing one a moment later, so the gap between the two would stretch and shrink by tiny amounts. A radio link measured that gap to within a single micrometer, thinner than a red blood cell. From those changes, scientists built the most detailed gravity map of any world ever made.

The map has a name for its biggest surprises. Mascons, short for mass concentrations, are dense lumps buried under old impact basins that tug harder than the rock around them. GRAIL also found a crust far more cracked and porous than anyone expected, and long buried ridges of ancient hardened magma.

## The best part was the kids

The two spacecraft were not named by scientists. Fourth graders in Bozeman, Montana won a national contest to name them Ebb and Flow.

Each spacecraft also carried a camera called MoonKAM, and its only job was to let middle-school students, not researchers, choose what to photograph. A classroom picked a target, the request went to an operations center in San Diego run by Sally Ride's team, and the spacecraft turned and took the shot. More than 2,700 schools in 52 countries flew it. When the mission ended, the crash site was named for Sally Ride.

## What I built

The site has three parts. The one people stay on is the gravity map. It is GRAIL's real data, the actual file NASA publishes for visualization, wrapped onto a globe you spin with your mouse and zoom like Google Earth. Pins sit on real basins and craters, plus two human landmarks: Apollo 11's Tranquility Base and the ridge where GRAIL was deliberately crashed. Click one and the Moon flies to it.

The second part recreates a MoonKAM image request. You pick a target, watch the request uplink and the frame downlink, and receive a real photograph that a classroom received in 2012.

The third is the mission itself, launch to impact, told plainly.

## Keeping it honest

This was the rule I set, because I hoped to show it to people at NASA. Nothing invented. Every number traces to NASA or the mission record. Every image is NASA public domain, credited, with a clear note that the site is an independent tribute and not affiliated with NASA.

Two honest limits made the site better, not worse. Only two genuine MoonKAM frames survive in NASA's public library, so I used both rather than fake more, and the page says so. And the pins describe a feature by what its gravity means, a mass excess or a low, rather than claiming a precision I cannot stand behind.

## How it is put together

It is a small static site. The globe is three.js, with the Moon as a textured sphere I rotate by hand rather than with a heavy camera library. The gravity field and the surface are two real NASA textures I self-host, so the site does not lean on anyone else's server staying up.

The fiddly part was placing a pin at a real latitude and longitude so it lands on the right spot on the map. That meant matching the exact way the image wraps onto the sphere, and rotating with quaternions so a pin can face you without the math flipping north and south. Apollo 11 sits exactly where it should, which is how I knew the alignment was right.

## What is left

The mission ended in 2012. The site is live, and I will keep adding features to the globe as I find good ones. The part I cannot script is whether it makes a ten year old ask a better question. That was the whole point of the mission, and it is the whole point of this.`,
};

export default article;
